/**
 * RA-2027 — plain-language names for the PBJ warning codes.
 *
 * Permanent UI, not a launch aid. Facilities get their PBJ numbers fixed once at go-live, but
 * therapists are added continuously, so misconfiguration is an ongoing condition rather than
 * something that clears.
 *
 * The vocabulary is fixed at 24 codes; the five named here are the ones that actually fire. An
 * unnamed code is humanised rather than hidden — the codes themselves are not PHI, and a customer
 * being told "24: unmapped pay type" is better served than one told nothing.
 *
 * Counts only, everywhere. Warning *detail* carries provider and patient ids; see
 * `parsePbjWarnings`, which reduces the header to numbers before it can reach here.
 */
const PBJ_WARNING_LABELS: Record<string, string> = {
  MISSING_PBJ_ID: 'no PBJ number set',
  MISSING_FACILITY_PBJ_NUMBER: 'facility has no PBJ number',
  UNMAPPED_JOB_TITLE_CODE: 'no discipline set',
  MISSING_HIRE_DATE: 'no hire date could be determined',
  DAILY_HOURS_OVER_CAP: 'more than 22.5 hours in one day',
};

export const pbjWarningLabel = (code: string): string => {
  const known = PBJ_WARNING_LABELS[code];
  if (known) return known;
  //  Unknown code: SOME_NEW_CODE -> "some new code". Readable, and still just a code.
  return code.toLowerCase().replace(/_/g, ' ');
};

/**
 * The headline count. Says "records" rather than "employees" because the same header mixes
 * per-provider skips with per-day hour caps, and calling a capped day an employee would be wrong.
 */
export const pbjWarningHeadline = (warningCount: number): string =>
  warningCount === 1
    ? '1 record was skipped or adjusted — this file may be incomplete.'
    : `${warningCount} records were skipped or adjusted — this file may be incomplete.`;

/**
 * Per-code lines, biggest count first. Counts only.
 *
 * The numeric filter deliberately repeats the one in `parsePbjWarnings`. That is not redundancy:
 * this is the function that renders, and it takes its input from the redux store rather than from
 * the header directly. Without it, a value that reached the store unfiltered would be interpolated
 * straight into the string — and `` `${['provider-9f3a1c', 'provider-4b7e02']}` `` is
 * `"provider-9f3a1c,provider-4b7e02"`, i.e. ids on screen. Two independent gates, because only one
 * of them is the last one.
 */
export const pbjWarningLines = (warnings: Record<string, number>): string[] =>
  Object.keys(warnings)
    .filter((code) => {
      const count = warnings[code];
      return typeof count === 'number' && Number.isFinite(count) && count > 0;
    })
    .sort((a, b) => warnings[b] - warnings[a] || a.localeCompare(b))
    .map((code) => `${pbjWarningLabel(code)}: ${warnings[code]}`);
